import React from 'react';

import Alert from 'react-bootstrap/Alert';
import SquareLoader from 'components/atoms/square_loader';

import { useBidderConsiderationDraft } from 'hooks/useBidderConsiderationDraft';

import type { BidderConsiderationTeamRow } from 'store/modules/bidding_summary_store/types';

import { formatIsoDateTimeBr } from 'services/format_iso_date_br';

import { Container } from './styles';

const MY_INSTRUCTION =
  'Escreva suas considerações após a análise dessa oportunidade';

interface BiddingSummaryBidderConsiderationProps {
  consideration?: string;
  teamConsiderations?: BidderConsiderationTeamRow[];
  isLoading?: boolean;
  isSaving?: boolean;
  isDeleting?: boolean;
  /** Primeira mensagem de erro priorizada: salvar > excluir > carregar suas > carregar equipe */
  errorMessage?: string;
  onSave: (consideration: string) => void;
  onDelete: () => void;
}

const BiddingSummaryBidderConsideration: React.FC<BiddingSummaryBidderConsiderationProps> = ({
  consideration = '',
  teamConsiderations = [],
  isLoading = false,
  isSaving = false,
  isDeleting = false,
  errorMessage,
  onSave,
  onDelete,
}) => {
  const showTeamSection = teamConsiderations.length > 0;
  const isBusy = isLoading || isSaving || isDeleting;
  const {
    text,
    setText,
    isTextareaFocused,
    setIsTextareaFocused,
    hasSavedText,
    isDirty,
    showSavedView,
    trimmedText,
    startEditMine,
    handleCancelEdit,
    maxLength,
  } = useBidderConsiderationDraft(consideration);

  const handleSaveRequest = (): void => {
    if (isBusy) return;
    if (hasSavedText && !isDirty) {
      handleCancelEdit();
      return;
    }
    onSave(text);
  };

  const handleEditorKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    if (isBusy) return;
    handleSaveRequest();
  };

  const handleDeleteClick = (): void => {
    if (isBusy || !hasSavedText) return;
    onDelete();
  };

  return (
    <Container>
      <div className="row">
        <div className="col-12">
          {errorMessage ? (
            <Alert variant="danger" className="small py-2 mb-3" role="alert">
              {errorMessage}
            </Alert>
          ) : null}
          {isLoading ? (
            <p className="mb-0 text-muted">Carregando considerações...</p>
          ) : (
            <>
              <section className="bidder-consideration-my-section" aria-label="Suas considerações">
                {showSavedView ? (
                  <>
                    <div className="bidder-consideration-mine-header d-flex flex-wrap justify-content-between align-items-center">
                      <h3 className="h6 font-weight-bold text-dark mb-0">Suas considerações</h3>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 bidder-consideration-edit-link bidding-summary-omit-from-print-share d-print-none"
                        onClick={startEditMine}
                        disabled={isBusy}
                        aria-label="Editar suas considerações"
                      >
                        <i className="material-icons bidder-consideration-edit-icon" aria-hidden>
                          edit
                        </i>
                        Editar
                      </button>
                    </div>
                    <div className="bidder-consideration-team-card bidder-consideration-mine-readonly">
                      <div className="bidder-consideration-team-card__body text-dark">
                        {(consideration || '').trim() || '—'}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-muted summary-field-label bidder-consideration-editor-lead">
                      {MY_INSTRUCTION}
                    </div>
                    <textarea
                      className="form-control bidder-consideration-textarea"
                      placeholder="Escreva aqui suas considerações"
                      value={text}
                      maxLength={maxLength}
                      onChange={(event) => setText(event.target.value)}
                      onKeyDown={handleEditorKeyDown}
                      onFocus={() => setIsTextareaFocused(true)}
                      onBlur={() => setIsTextareaFocused(false)}
                      disabled={isBusy}
                      aria-label="Considerações do licitante — Enter salva, Shift+Enter quebra linha"
                    />

                    <div className="bidder-consideration-actions-row bidding-summary-omit-from-print-share d-print-none">
                      <div className="bidder-consideration-actions-row__meta">
                        {isTextareaFocused && (
                          <span
                            className="bidder-consideration-char-count text-muted small"
                            aria-live="polite"
                          >
                            {text.length} / {maxLength}
                          </span>
                        )}
                      </div>
                      <div className="bidder-consideration-actions-row__buttons">
                        {hasSavedText && (
                          <button
                            type="button"
                            className="btn btn-link text-primary px-2 py-1 bidder-consideration-delete-btn"
                            onClick={handleDeleteClick}
                            disabled={isBusy}
                            aria-label="Excluir suas considerações"
                          >
                            {isDeleting ? (
                              <SquareLoader customClass="delete-question-loading" />
                            ) : (
                              <>
                                <i
                                  className="material-icons mr-1"
                                  aria-hidden
                                  style={{ fontSize: '1.125rem' }}
                                >
                                  delete_outline
                                </i>
                                Excluir
                              </>
                            )}
                          </button>
                        )}
                        {hasSavedText && (
                          <button
                            type="button"
                            className="btn btn-link px-2 py-1"
                            onClick={handleCancelEdit}
                            disabled={isBusy}
                          >
                            Cancelar
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-primary bidder-consideration-save-btn px-3"
                          onClick={handleSaveRequest}
                          disabled={
                            isBusy ||
                            (!trimmedText && !hasSavedText) ||
                            (hasSavedText && !isDirty)
                          }
                        >
                          {isSaving ? 'Salvando...' : 'Salvar'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </section>

              {showTeamSection && (
                <section className="bidder-consideration-team-section" aria-label="Considerações da equipe">
                  <h3 className="h6 font-weight-bold text-dark bidder-consideration-team-heading">
                    Análise e Considerações da equipe
                  </h3>
                  <div className="bidder-consideration-team-entries">
                    {teamConsiderations.map((row) => (
                      <div key={row.id} className="bidder-consideration-team-card">
                        <div className="bidder-consideration-team-card__header d-flex flex-wrap justify-content-between align-items-baseline">
                          <span className="font-weight-bold text-dark">{row.contact_name}</span>
                          <time
                            className="text-muted small text-nowrap ml-2"
                            dateTime={row.updated_at}
                          >
                            {formatIsoDateTimeBr(row.updated_at)}
                          </time>
                        </div>
                        <div className="bidder-consideration-team-card__body text-dark mt-2">
                          {row.consideration?.trim() || '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </Container>
  );
};

export default BiddingSummaryBidderConsideration;
